<template>
  <div
    :data-test-id="generateDataTestId(dataTestId, 'card')"
    class="am3-detail-card min-h-[200px] bg-surface-light py-8 px-8 border border-weak text-sm rounded-md relative"
  >
    <!-- Title and link section -->
    <div
      v-if="title && !props.isLoading"
      class="text-lg font-medium mb-4 flex flex-row items-center justify-between"
    >
      <h2
        class="text-primary text-sm"
        :data-test-id="generateDataTestId(dataTestId, 'title')"
      >
        {{ title }}
      </h2>
      <button
        v-if="link"
        class="text-brand hover:underline focus:underline focus:outline-brand p-2 box-content text-sm"
        :data-test-id="generateDataTestId(dataTestId, 'link')"
        @click="handleLinkClick"
      >
        {{ link.text }}
      </button>
    </div>

    <!-- Heading, status and toggle section -->
    <div
      v-if="!props.isLoading"
      :class="collapsible ? 'bg-surface p-4 rounded-md' : ''"
    >
      <div class="text-md font-bold flex flex-row items-center justify-between">
        <div class="flex items-center justify-between w-full gap-2">
          <div
            class="flex items-center"
            :class="collapsible ? 'cursor-pointer' : ''"
            @click="collapsible ? toggleOpen() : null"
          >
            <h3 class="text-primary">
              {{ heading }}
            </h3>

            <!-- Animated chevron icon for collapsible cards -->
            <div
              v-if="collapsible"
              class="ml-2 flex items-center transition-transform duration-300"
            >
              <AM3Icon
                type="chevron-down"
                class="w-4 h-4 transform transition-transform duration-300 ease-in-out"
                :class="{ 'rotate-180': !isOpen }"
              />
            </div>
          </div>

          <!-- Use the status slot if provided -->
          <slot name="status">
            <span
              v-if="status"
              class="ml-4 text-sm font-normal text-secondary inline-flex items-center"
              :class="{
                'text-success': status === 'Subscribed',
                'text-primary': status === 'Not Subscribed',
                'text-danger': status === 'Unsubscribed',
              }"
              :data-test-id="generateDataTestId(dataTestId, 'status')"
            >
              <AM3Icon
                v-if="hasStatusIcon"
                :type="statusIconType"
                class="w-4 h-4 mr-2"
              />
              {{ status }}
            </span>
          </slot>
        </div>
      </div>

      <!-- Data rows with slide-down transition -->
      <transition
        name="slide-fade"
        @enter="onEnter"
        @leave="onLeave"
        @before-enter="beforeEnter"
        @before-leave="beforeLeave"
      >
        <div
          v-if="isOpen"
          class="mt-0"
        >
          <div
            ref="contentRef"
            class="content-wrapper"
          >
            <!-- Content area with optional min-height for pagination -->
            <div
              :style="isPaginationEnabled ? { minHeight: minContentHeight } : {}"
            >
              <!-- Use slot with scoped data for pagination -->
              <slot
                :items="paginatedItems"
                :current-page="currentPage"
                :total-pages="totalPages"
                :items-per-page="itemsPerPage"
                :left-header="leftHeader"
                :right-header="rightHeader"
              />
            </div>

            <!-- Pagination slot -->
            <div
              v-if="showPagination"
              class="mt-4"
            >
              <slot
                name="pagination"
                :current-page="currentPage"
                :total-pages="totalPages"
                :update-page="(page: number) => emit('update:currentPage', page)"
              />
            </div>
          </div>
        </div>
      </transition>
    </div>
    <div
      v-else
      class="size-full flex text-center flex-col items-center justify-center"
    >
      <AM3Loading />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { generateDataTestId } from '@/components/am3-generic/core/dataTestId'

/**
 * Interface for link object structure
 */
interface Link {
  /**
   * URL to navigate to
   * Optional if you're using custom actions (modal, etc.) without navigation
   */
  path?: string
  /**
   * Text to display for the link
   */
  text: string
  /**
   * Optional callback function to be triggered on click
   */
  onClick?: () => void
  /**
   * Optional flag to indicate if the link should use custom handling (e.g., modal)
   * If true, prevents default navigation behavior
   */
  customAction?: boolean
}

/**
 * Interface for a data item in the detail card
 */
interface DataItem {
  /**
   * Label for the data field
   */
  label: string
  /**
   * Value of the data field
   */
  value: string | number
}

/**
 * Props for AM3DetailCard component
 */
interface Props {
  /**
   * Optional title for the card (displayed above heading)
   */
  title?: string
  /**
   * Heading title for the card (displayed below title)
   */
  heading?: string
  /**
   * Optional link object with path, text, and optional onClick handler
   */
  link?: Link
  /**
   * Optional data-test-id for testing
   */
  dataTestId?: string
  /**
   * Optional status text displayed opposite the heading
   */
  status?: string
  /**
   * Whether the content is expanded or collapsed
   */
  open?: boolean
  /**
   * Whether the card can be collapsed/expanded
   * If false, the toggle button won't be displayed
   */
  collapsible?: boolean
  /**
   * Custom icon type for status (if not using standard subscription status icons)
   */
  statusIcon?: string

  /**
   * Options for the paywall component
   */
  paywallOptions?: null | object

  // true/false state for loading
  isLoading?: boolean

  /**
   * Enable pagination with automatic data slicing
   * Pass the full data array to paginate
   */
  paginatedData?: any[]

  /**
   * Number of items to show per page (default: 5)
   */
  itemsPerPage?: number

  /**
   * Current page number (use v-model:currentPage)
   */
  currentPage?: number

  /**
   * Minimum height for content area to ensure consistent card heights
   * Only applied when pagination is enabled (default: '280px')
   */
  minContentHeight?: string

  /**
   * Left column header text for paginated data tables
   * Example: "Name", "Customer", etc.
   */
  leftHeader?: string

  /**
   * Right column header text for paginated data tables
   * Example: "Total Spent", "Referrals", "Events", etc.
   */
  rightHeader?: string
}

// Define props with default values
const props = withDefaults(defineProps<Props>(), {
  dataTestId: 'am3-detail-card',
  open: true,
  collapsible: false,
  isLoading: false,
  itemsPerPage: 5,
  currentPage: 1,
  minContentHeight: '280px',
})

// Define emits
const emit = defineEmits<{
  (e: 'linkClick', link: Link): void
  (e: 'update:open', value: boolean): void
  (e: 'update:currentPage', value: number): void
}>()

// Reactive state for open/closed status
const isOpen = ref(props.open)
const contentRef = ref<HTMLElement | null>(null)

// Pagination computed properties
const isPaginationEnabled = computed(() => {
  return props.paginatedData && props.paginatedData.length > 0
})

const paginatedItems = computed(() => {
  if (!isPaginationEnabled.value) return []

  const start = (props.currentPage - 1) * props.itemsPerPage
  const end = start + props.itemsPerPage
  return props.paginatedData!.slice(start, end)
})

const totalPages = computed(() => {
  if (!isPaginationEnabled.value) return 0
  return Math.ceil(props.paginatedData!.length / props.itemsPerPage)
})

const showPagination = computed(() => {
  return isPaginationEnabled.value && totalPages.value > 1
})

// Computed properties for status icons
const hasStatusIcon = computed(() => {
  return props.statusIcon || ['Subscribed', 'Unsubscribed', 'Not Subscribed'].includes(props.status || '')
})

const statusIconType = computed(() => {
  if (props.statusIcon) {
    return props.statusIcon
  }

  switch (props.status) {
    case 'Subscribed':
      return 'check-circle'
    case 'Unsubscribed':
      return 'x-circle'
    case 'Not Subscribed':
      return 'exclamation-circle'
    default:
      return 'information-circle'
  }
})

// Watch for changes to the open prop
watch(() => props.open, (newValue) => {
  isOpen.value = newValue
})

// Reset to page 1 when paginated data changes
watch(() => props.paginatedData, () => {
  if (isPaginationEnabled.value && props.currentPage !== 1) {
    emit('update:currentPage', 1)
  }
}, { deep: true })

/**
 * Toggle the open/closed state of the card
 */
const toggleOpen = () => {
  isOpen.value = !isOpen.value
  emit('update:open', isOpen.value)
}

/**
 * Animation hooks for the transition
 */
const beforeEnter = (el: HTMLElement) => {
  el.style.height = '0'
  el.style.opacity = '0'
}

const onEnter = (el: HTMLElement, done: () => void) => {
  const contentHeight = contentRef.value?.scrollHeight || 0

  // First step: animate height from 0 to content height
  el.style.height = `${contentHeight}px`
  el.style.opacity = '1'

  // Use requestAnimationFrame to ensure transition happens
  requestAnimationFrame(() => {
    // Add transition
    el.style.transition = 'height 0.3s ease-in-out, opacity 0.25s ease-in-out'

    // Listen for the end of transition to call done()
    el.addEventListener('transitionend', function onEnd(event) {
      if (event.propertyName === 'height') {
        el.removeEventListener('transitionend', onEnd)
        el.style.height = 'auto' // Set height to auto to handle content changes
        done()
      }
    })
  })
}

const beforeLeave = (el: HTMLElement) => {
  // Set explicit height instead of auto to enable transition
  el.style.height = `${el.scrollHeight}px`
  el.style.overflow = 'hidden'
}

const onLeave = (el: HTMLElement, done: () => void) => {
  // Force a reflow to ensure the browser registers the beforeLeave changes
  el.scrollHeight

  // Add transition
  el.style.transition = 'height 0.3s ease-in-out, opacity 0.2s ease-in-out'

  // Animate height to 0 and fade out
  el.style.height = '0'
  el.style.opacity = '0'

  // Listen for transition end to call done()
  el.addEventListener('transitionend', function onEnd(event) {
    if (event.propertyName === 'height') {
      el.removeEventListener('transitionend', onEnd)
      done()
    }
  })
}

/**
 * Handle click on the link button
 *
 * This method handles both standard link behavior and custom actions like opening modals
 * 1. If a custom onClick handler is provided, it will be called
 * 2. The linkClick event is emitted with the link object for external handling
 * 3. For custom actions (modals, etc.), the customAction flag can be set to true
 */
const handleLinkClick = () => {
  if (props.link) {
    // If there's a custom onClick handler in the link object, call it
    if (props.link.onClick) {
      props.link.onClick()
    }

    // Emit linkClick event with the link object
    // This allows parent components to handle modals or other custom actions
    emit('linkClick', props.link)
  }
}
</script>

<style>
/* Custom styles for the slide-down animation */
.slide-fade-enter-active,
.slide-fade-leave-active {
  will-change: height, opacity;
  overflow: hidden;
}

.content-wrapper {
  will-change: transform, opacity;
  transform-origin: top center;
}

/* Add some fancy shadow effect during animation */
.slide-fade-enter-active .content-wrapper {
  animation: content-appear 0.3s ease-out forwards;
}

.slide-fade-leave-active .content-wrapper {
  animation: content-disappear 0.25s ease-in forwards;
}

@keyframes content-appear {
  0% {
    transform: translateY(-10px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes content-disappear {
  0% {
    transform: translateY(0);
    opacity: 1;
  }
  100% {
    transform: translateY(-10px);
    opacity: 0;
  }
}
</style>
